import {
  QueryStatus,
  UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import type {AxiosResponse} from 'axios'
import {
  EndpointMetaDataType,
  EndpointsItemsTypes,
  EndpointsSearchRequest,
} from 'interfaces'
import {useEffect, useMemo, useState} from 'react'
import {EndpointsService} from 'services/api'
import {transformEndpointsListResponse} from 'features/Endpoints/transformer'

export const ENDPOINTS_METADATA_QUERY_KEY = ['endpoints-metadata']
export const endpointsMetadataQueryFn = (): Promise<
  AxiosResponse<EndpointMetaDataType>
> => EndpointsService.getEndpointMetaData()

export const useEndpointsMetadataQuery =
  (): UseQueryResult<EndpointMetaDataType> => {
    return useQuery(ENDPOINTS_METADATA_QUERY_KEY, endpointsMetadataQueryFn, {
      select: data => data.data,
    })
  }

interface EndpointsSearchQueryResult {
  filteredEndpoints: EndpointsItemsTypes[]
  status: QueryStatus
  fetchNextPage: () => void
  error: string
  hasNextPage: boolean
  isLoading: boolean
  setFilteredEndpointsState: (arg: EndpointsItemsTypes[]) => void
  refetch: () => void
  // True only once the server answered with no endpoints at all; read from the
  // query itself, not the list state, which fills in a render later.
  hasNoEndpoints: boolean
}
export const GET_ENDPOINTS_QUERY_KEY = 'getEndpointsSearch'
export const useEndpointsSearchQuery = (
  filterOptions: EndpointsSearchRequest,
): EndpointsSearchQueryResult => {
  const [filteredEndpoints, setFilteredEndpoints] = useState<
    EndpointsItemsTypes[]
  >([])
  const {
    data: endpointsSearchRes,
    status,
    fetchNextPage,
    error,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: [GET_ENDPOINTS_QUERY_KEY, filterOptions],
    queryFn: ({pageParam}) =>
      EndpointsService.getEndpointsSearch({
        ...filterOptions,
        after: pageParam,
      }),
    getNextPageParam: lastPage => lastPage?.data?.data?.pageInfo?.endCursor,
  })

  // selectors
  useEffect(() => {
    const newEndpoints =
      endpointsSearchRes?.pages?.flatMap(page => {
        return transformEndpointsListResponse(page?.data?.data?.endpoints || [])
      }) || []
    // Keep ticked rows ticked when the cached pages change underneath them
    // (for example a toggle marked pending while rows were being selected).
    setFilteredEndpoints(previous => {
      const selected = new Set(
        previous
          .filter(endpoint => endpoint.isSelected)
          .map(endpoint => `${endpoint.agentUUID}_${endpoint.userUUID}`),
      )
      if (!selected.size) return newEndpoints
      return newEndpoints.map(endpoint =>
        selected.has(`${endpoint.agentUUID}_${endpoint.userUUID}`)
          ? {...endpoint, isSelected: true}
          : endpoint,
      )
    })
  }, [endpointsSearchRes?.pages])

  const hasNextPage = useMemo(() => {
    const lastPage = endpointsSearchRes?.pages?.at(-1)
    return Boolean(lastPage?.data?.data?.pageInfo?.hasNextPage)
  }, [endpointsSearchRes?.pages])

  const setFilteredEndpointsState = (
    endpoints: EndpointsItemsTypes[],
  ): void => {
    setFilteredEndpoints(endpoints)
  }

  const hasNoEndpoints = useMemo(
    () =>
      Boolean(endpointsSearchRes?.pages?.length) &&
      Boolean(
        endpointsSearchRes?.pages?.every(
          page => !page?.data?.data?.endpoints?.length,
        ),
      ),
    [endpointsSearchRes?.pages],
  )

  return {
    hasNoEndpoints,
    filteredEndpoints,
    status,
    fetchNextPage,
    error: error as string,
    hasNextPage,
    isLoading,
    setFilteredEndpointsState,
    refetch,
  }
}
